import * as React from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { fetchTerminalsByPort, Terminal, TerminalQueryKeys } from 'services/clients/terminal';
import _ from 'lodash';
import { FilterDropdown } from 'components/tailwind-ui/FilterDropdown';
import { NestedFilter } from '../ContainerFilters/NestedFilter';

interface TerminalGroup {
  name: string;
  code: string;
  portCodes: Set<string>;
  terminals: Terminal[];
  throughput: number;
}

interface Props {
  setValues: (values: { terminals: string[]; portCodes: string[] }) => void;
  values: { terminals: string[]; portCodes: string[] };
  label?: string;
}

export const TerminalFilter: React.FC<Props> = (props) => {
  const { setValues, values, label } = props;
  const terminalsQuery: UseQueryResult<Terminal[]> = useQuery({
    queryKey: [TerminalQueryKeys.TerminalsByPort],
    queryFn: ({ signal }) => fetchTerminalsByPort({ signal }),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
    refetchOnWindowFocus: false,
  });

  // Group terminals
  const terminalGroups = React.useMemo(() => {
    if (_.isNil(terminalsQuery.data)) {
      return {};
    }

    const groups: Record<string, TerminalGroup> = {};
    terminalsQuery.data.forEach((terminal: Terminal) => {
      const code = terminal.port.portCollection?.code || terminal.port.portCode;
      if (!code) {
        return;
      }

      if (code in groups) {
        groups[code].portCodes.add(terminal.port.portCode);
        groups[code].terminals = _.sortBy([...groups[code].terminals, terminal], 'name');
      } else {
        groups[code] = {
          name: terminal.port.portCollection?.name || terminal.port.name,
          code,
          portCodes: new Set([terminal.port.portCode]),
          terminals: [terminal],
          throughput: terminal.port.throughput,
        };
      }
    });

    return groups;
  }, [terminalsQuery.data]);

  const handleClearFilter = () => {
    setValues({ terminals: [], portCodes: [] });
  };

  const handlePortCheckboxOnChange = (checked: boolean, terminalGroup: TerminalGroup) => {
    const terminalPickupKeys = terminalGroup.terminals.map((t) => t.key);
    const portPickupCodes = terminalGroup.portCodes;
    if (checked) {
      setValues({
        portCodes: [...new Set([...values.portCodes, ...portPickupCodes])],
        terminals: [...new Set([...values.terminals, ...terminalPickupKeys])],
      });
    } else {
      setValues({
        portCodes: values.portCodes.filter((e: string) => !portPickupCodes.has(e)),
        terminals: values.terminals.filter((e: string) => !terminalPickupKeys.includes(e)),
      });
    }
  };

  const handleTerminalCheckboxOnChange = (
    checked: boolean,
    terminal: Terminal,
    terminalGroup: TerminalGroup
  ) => {
    let newTerminalPickupKeys: string[] = [];
    if (checked) {
      newTerminalPickupKeys = [...values.terminals, terminal.key];
    } else {
      newTerminalPickupKeys = values.terminals.filter((e: string) => e !== terminal.key);
    }

    // If all the terminals of a port collection are selected then we add the port code filter
    if (terminalGroup.terminals.every((t) => newTerminalPickupKeys.includes(t.key))) {
      handlePortCheckboxOnChange(true, terminalGroup);
    }
    // If all the terminals of a port collection have been deselected then we remove the port code filter
    else if (!terminalGroup.terminals.some((t) => newTerminalPickupKeys.includes(t.key))) {
      handlePortCheckboxOnChange(false, terminalGroup);
    } else {
      setValues({
        terminals: newTerminalPickupKeys,
        portCodes: [],
      });
    }
  };

  const initialValue: Record<string, number> = Object.values(terminalGroups).reduce<
    Record<string, number>
  >((acc, group: TerminalGroup) => {
    acc[group.name] = 0;
    return acc;
  }, {});

  // Calculate number of terminals per port collection
  const countPerPortCollection = Object.values(terminalGroups).reduce(
    (acc: Record<string, number>, group: TerminalGroup) => {
      acc[group.name] = group.terminals
        .map((t) => t.key)
        .reduce((count, k) => count + (values.terminals.includes(k) ? 1 : 0), 0);
      return acc;
    },
    initialValue
  );

  const sortedTerminalGroupItems = React.useMemo(() => {
    return _.orderBy(Object.values(terminalGroups), ['throughput', 'name'], ['desc', 'asc']);
  }, [terminalGroups]);

  return (
    <FilterDropdown
      label={label || 'Terminal'}
      count={values.terminals.length || values.portCodes.length}
      clear={handleClearFilter}
    >
      <div className="flex flex-col p-0 max-h-[300px] h-48 overflow-y-auto scrollbar">
        <p className="text-xs text-gray-700 p-3 leading-4">
          Select a port to specify
          <br />a terminal
        </p>
        {sortedTerminalGroupItems.map((terminalGroup: TerminalGroup) => {
          return (
            <FormControlLabel
              classes={{
                label: 'font-medium text-sm w-full',
                root: 'm-0 hover:bg-blue-200',
              }}
              sx={{
                '.MuiTypography-root div': {
                  paddingLeft: '0 !important',
                  paddingY: '0 !important',
                },
              }}
              control={
                <Checkbox
                  size="small"
                  disableRipple
                  className="cursor-default p-2 m-2 ml-3"
                  indeterminate={
                    (countPerPortCollection[terminalGroup.name] > 0 ||
                      values.portCodes.some((portCode) => terminalGroup.portCodes.has(portCode))) &&
                    countPerPortCollection[terminalGroup.name] < terminalGroup.terminals.length
                  }
                  checked={
                    [...terminalGroup.portCodes].every((portCode) =>
                      values.portCodes.includes(portCode)
                    ) || values.portCodes.some((portCode) => terminalGroup.portCodes.has(portCode))
                  }
                  onChange={(e) => handlePortCheckboxOnChange(e.target.checked, terminalGroup)}
                  sx={{
                    svg: {
                      display: 'none',
                    },
                    '&.MuiCheckbox-root input': {
                      opacity: 1,
                      borderRadius: '0.25rem',
                      borderColor: '#CDD0E0', // gray-300,
                    },
                  }}
                />
              }
              label={
                <NestedFilter
                  label={terminalGroup.name}
                  className="overflow-y-scroll scrollbar border-gray-300 p-0 rounded-md max-h-48"
                  divStyleOverride={{ minWidth: '192px' }}
                  onClick={(e) => e.preventDefault()}
                  isActive={values.terminals.some((e: string) =>
                    terminalGroup.terminals.map((t) => t.key).includes(e)
                  )}
                  showNewArrowIcon
                >
                  <FormGroup className="p-0">
                    {terminalGroup.terminals.map((terminal) => (
                      <FormControlLabel
                        classes={{
                          label: 'font-medium text-sm',
                          root: 'm-0 p-0 hover:bg-blue-200 cursor-default',
                        }}
                        sx={{
                          '.MuiTypography-root div': {
                            paddingLeft: '0 !important',
                            paddingY: '0 !important',
                          },
                        }}
                        control={
                          <Checkbox
                            size="small"
                            disableRipple
                            className="cursor-default p-2 m-2 ml-3"
                            checked={
                              values.terminals.includes(terminal.key) ||
                              (values.portCodes.some((portCode) =>
                                terminalGroup.portCodes.has(portCode)
                              ) &&
                                !terminalGroup.terminals.some((v) =>
                                  values.terminals.includes(v.key)
                                ))
                            }
                            onChange={(e) =>
                              handleTerminalCheckboxOnChange(
                                e.target.checked,
                                terminal,
                                terminalGroup
                              )
                            }
                            sx={{
                              svg: {
                                display: 'none',
                              },
                              '&.MuiCheckbox-root input': {
                                opacity: 1,
                                borderRadius: '0.25rem',
                                borderColor: '#CDD0E0', // gray-300,
                              },
                            }}
                          />
                        }
                        label={terminal.name}
                        key={terminal.key}
                      />
                    ))}
                  </FormGroup>
                </NestedFilter>
              }
              key={terminalGroup.code}
            />
          );
        })}
      </div>
    </FilterDropdown>
  );
};
TerminalFilter.displayName = 'ContainerFilters.TerminalFilter';
